import styled, { keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/*
 * version3's Analytics page (routes/analytics.tsx). Radii follow its scale,
 * where --radius is 0.875rem: rounded-lg 14px, rounded-xl 18px, and the
 * surface-card utility at 22px.
 */
const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const BORDER = 'var(--hd-border)';
const CARD = 'var(--hd-card)';
const PRIMARY = 'var(--hd-primary)';
const SECONDARY = 'var(--hd-secondary)';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* version3's .surface-card. */
const surfaceCard = `
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};
  box-shadow: var(--hd-shadow-card);
`;

export const StyledAnalytics = styled.div`
  ${brandTheme}
  min-height: 100%;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: ${FG};
  background: var(--hd-background);
  -webkit-font-smoothing: antialiased;

  .analytics-main {
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
    padding: 32px;
    animation: ${fadeUp} 0.5s ease-out both;

    @media (max-width: 767px) {
      padding: 24px 16px;
    }
  }

  .notice {
    margin: 0 0 12px;
    font-size: 12px;
    color: var(--hd-destructive);
  }
`;

/* ---------------- Header ---------------- */

export const StyledPageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }

  h1 {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  .subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};

    b {
      font-weight: 500;
      color: ${FG};
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const StyledRangeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${BORDER};
  border-radius: 18px;
  background: ${CARD};

  button {
    padding: 6px 12px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: ${FG};
    }

    &.active {
      color: ${FG};
      background: ${SECONDARY};
    }
  }
`;

export const StyledExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid ${BORDER};
  border-radius: 18px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED_FG};
  background: ${CARD};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: color-mix(in oklab, var(--hd-accent) 40%, transparent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* ---------------- KPIs ---------------- */

export const StyledKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (min-width: 1024px) {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
  }
`;

export const StyledKpiCard = styled.div`
  ${surfaceCard}
  position: relative;
  overflow: hidden;
  padding: 20px;
  ${({ $accent }) => ($accent ? 'background: var(--hd-gradient-soft);' : '')}

  .kpi-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .kpi-label {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${MUTED_FG};
  }

  .kpi-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 14px;
    color: ${PRIMARY};
    background: ${({ $accent }) => ($accent ? `color-mix(in oklab, ${CARD} 70%, transparent)` : SECONDARY)};

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .kpi-value {
    margin-top: 12px;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    letter-spacing: -0.025em;
    font-variant-numeric: tabular-nums;
    color: ${FG};
  }

  .kpi-delta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
    color: ${({ $up }) => ($up ? 'oklch(0.55 0.16 160)' : 'var(--hd-destructive)')};

    svg {
      width: 12px;
      height: 12px;
    }

    span {
      font-weight: 400;
      color: ${MUTED_FG};
    }

    &.none {
      color: ${MUTED_FG};
      font-weight: 400;
    }
  }
`;

/* ---------------- Volume + funnel ---------------- */

export const StyledGrid12 = styled.section`
  display: grid;
  gap: 24px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));

    .span-8 {
      grid-column: span 8 / span 8;
    }

    .span-7 {
      grid-column: span 7 / span 7;
    }

    .span-5 {
      grid-column: span 5 / span 5;
    }

    .span-4 {
      grid-column: span 4 / span 4;
    }
  }

  .stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
  }
`;

export const StyledSurface = styled.div`
  ${surfaceCard}
  min-width: 0;
  padding: ${({ $pad }) => $pad || '24px'};

  h2 {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    text-transform: none;
    color: ${FG};
  }

  .card-sub {
    margin: 2px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  .card-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
  }

  .legend {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 12px;
    color: ${MUTED_FG};

    span {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    i {
      width: 8px;
      height: 8px;
      border-radius: 999px;
    }
  }

  .chart {
    width: 100%;
    height: 224px;
  }

  .chart-empty {
    display: grid;
    height: 224px;
    place-items: center;
    font-size: 13px;
    color: ${MUTED_FG};
  }

  .funnel-sub {
    margin: 2px 0 20px;
  }
`;

export const StyledFunnelBar = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  .funnel-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    font-size: 12px;
  }

  .funnel-label {
    font-weight: 500;
    color: ${FG};
  }

  .funnel-value {
    font-variant-numeric: tabular-nums;
    color: ${MUTED_FG};

    b {
      font-weight: 500;
      color: ${FG};
    }
  }

  .track {
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: ${SECONDARY};
  }

  .fill {
    height: 100%;
    border-radius: 999px;
    background: var(--hd-gradient-houdiny);
    transition: width 0.5s ease;
  }
`;

/* ---------------- Channel breakdown + top campaigns ---------------- */

export const StyledSectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  h2 {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    text-transform: none;
    color: ${FG};
  }

  a {
    font-size: 12px;
    color: ${PRIMARY};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const StyledChannelCard = styled.div`
  ${surfaceCard}
  padding: 20px;

  .channel-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .channel-icon {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 14px;
    color: ${({ $color }) => $color || PRIMARY};
    background: ${({ $color }) => ($color ? `color-mix(in oklab, ${$color} 12%, transparent)` : SECONDARY)};

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .channel-name {
    font-size: 14px;
    font-weight: 500;
    color: ${FG};
  }

  .channel-share {
    margin-left: auto;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    padding-top: 12px;
    border-top: 1px solid ${BORDER};
  }

  .stat-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: ${MUTED_FG};
  }

  .stat-value {
    margin-top: 2px;
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${FG};

    &.accent {
      color: ${({ $color }) => $color || PRIMARY};
    }
  }
`;

export const StyledTopList = styled.div`
  ${surfaceCard}
  overflow: hidden;

  .top-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    color: inherit;
    text-decoration: none;
    transition: background 0.15s ease;

    &:not(:last-child) {
      border-bottom: 1px solid ${BORDER};
    }

    &:hover {
      background: color-mix(in oklab, var(--hd-accent) 40%, transparent);
    }
  }

  .top-avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    object-fit: cover;
  }

  .top-main {
    flex: 1;
    min-width: 0;
  }

  .top-name,
  .top-sub {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .top-name {
    font-size: 14px;
    font-weight: 500;
    color: ${FG};
  }

  .top-sub {
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .top-rate {
    text-align: right;

    b {
      display: block;
      font-size: 12px;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      color: ${PRIMARY};
    }

    span {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.025em;
      color: ${MUTED_FG};
    }
  }

  .top-empty {
    padding: 32px 16px;
    font-size: 13px;
    text-align: center;
    color: ${MUTED_FG};
  }
`;
